import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  SearchCallback,
  SearchSelectItem,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import defaultContent from "../../../content/actionReportGroupSop";
import navigationContent from "../../../content/navigation";
import { useAppDispatch, useAppSelector } from "../../../store";
import { showProfile } from "../../../reducers/profile";
import { PROFILE_TABS } from "../../../reducers/downlineProfile";
import { selectGroupLeader } from "../../../reducers/groupLeaderFilter";
import { ActionReportListView } from "../common/ActionReportListView/ActionReportListView";
import type { BaseABOData } from "../common/ActionReportListView/ActionReportListView";
import type { FieldConfig } from "../common/ActionReportListView/types";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { GroupLeaderFilter } from "../common/GroupLeaderFilter/GroupLeaderFilter";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import "./GroupSopReport.scss";
import { IconArrowLink } from "../../../common/icons/IconArrowLink/IconArrowLink";
import { useIsMobile } from "../hooks/useIsMobile";
import { GroupSopOverview } from "./OverviewCard/OverviewCard";
import {
  GROUP_SOP_MOCK_DATA,
  GROUP_SOP_MOCK_LEADERS,
  GROUP_SOP_MOCK_LEADER_TREE,
  getGroupSopMockLeaderAbo,
  mockForcastedSop,
  mockForcastedSopByLeader,
} from "./_mockdata";
import { SOP_TABS } from "./sopTabs";

type SortDirection = "asc" | "desc";

export type GroupSopRecord = BaseABOData & {
  personalPV: number | "--";
  customerPV: number;
  currentPPV: number | "--";
  currentSopPv: number;
  expectedSopPv: number;
  productImages: string[];
  sopProductCount: number;
  upForRenewal: boolean;
};

const MAX_SOP_PRODUCT_PREVIEWS = 4;

export const getSopProductPreview = (images: string[], total: number) => {
  return {
    images: images.slice(0, MAX_SOP_PRODUCT_PREVIEWS),
    additionalProductCount: total > MAX_SOP_PRODUCT_PREVIEWS ? total - MAX_SOP_PRODUCT_PREVIEWS : 0,
  };
};

const SopProductPreviews = ({
  images,
  total,
  label,
  onClick,
}: {
  images: string[];
  total: number;
  label: string;
  onClick: () => void;
}) => {
  const { images: productImages, additionalProductCount } = getSopProductPreview(images, total);

  const isMobile = useIsMobile();

  return isMobile ? (
    <button className="GroupSopReport__productLink GroupSopReport__productLink--mobile" onClick={onClick}>
      <span className="GroupSopReport__productLinkText">{label}</span>
      <div className="GroupSopReport__productImages" aria-hidden="true">
        {productImages.map((image) => (
          <img key={image} className="GroupSopReport__productImage" src={image} alt="" />
        ))}
        {additionalProductCount > 0 && <span className="GroupSopReport__productCount">+{additionalProductCount}</span>}
      </div>

      <IconArrowLink iconColor="blue" weight="regular" />
    </button>
  ) : (
    <button className="GroupSopReport__productPreview" onClick={onClick}>
      <div className="GroupSopReport__productImages" aria-hidden="true">
        {productImages.map((image) => (
          <img key={image} className="GroupSopReport__productImage" src={image} alt="" />
        ))}
        {additionalProductCount > 0 && <span className="GroupSopReport__productCount">+{additionalProductCount}</span>}
      </div>
      <div className="GroupSopReport__productLink">
        <span className="GroupSopReport__productLinkText">{label}</span>
        <IconArrowLink iconColor="blue" weight="regular" />
      </div>
    </button>
  );
};

const GROUP_SOP_TAB_CONFIG = {
  [SOP_TABS.ORDER_RENEWAL]: {
    selectedKpis: ["personalPV", "customerPV", "currentSopPv", "expectedSopPv", "sopProducts"],
  },
  [SOP_TABS.PV_THIS_MONTH]: {
    selectedKpis: ["personalPV", "customerPV", "currentSopPv", "expectedSopPv", "sopProducts"],
  },
  [SOP_TABS.NO_PV_THIS_MONTH]: {
    selectedKpis: ["personalPV", "customerPV", "sopProducts"],
  },
} satisfies Record<SOP_TABS, { selectedKpis: readonly string[] }>;

const formatSopValue = (value: number | "--", locale: string): string =>
  value === "--" ? value : new Intl.NumberFormat(locale).format(value);

const getSortValue = (record: GroupSopRecord, kpi: string): number | string => {
  if (kpi === "aboName") return record.aboName;
  if (kpi === "sopProducts") return record.sopProductCount;
  const value = record[kpi];
  return typeof value === "number" ? value : 0;
};

export const createGroupSopFieldConfig = (
  tableColumns: Record<string, string>,
  onProductClick: (row: BaseABOData) => void,
  includedKpis: readonly string[] = GROUP_SOP_TAB_CONFIG[SOP_TABS.ORDER_RENEWAL].selectedKpis,
): FieldConfig[] => {
  const columns: FieldConfig[] = [
    { id: "aboName", kpi: "aboName", title: tableColumns["aboName"], sortable: true },
    {
      id: "personalPV",
      kpi: "personalPV",
      title: tableColumns["personalPV"],
      sortable: true,
      renderValue: (value) => value,
    },
    {
      id: "customerPV",
      kpi: "customerPV",
      title: tableColumns["customerPV"],
      sortable: true,
      renderValue: (value) => value,
    },
    {
      id: "currentSopPv",
      kpi: "currentSopPv",
      title: tableColumns["currentSopPv"],
      sortable: true,
      renderValue: (value) => value,
    },
    {
      id: "expectedSopPv",
      kpi: "expectedSopPv",
      title: tableColumns["expectedSopPv"],
      sortable: true,
      renderValue: (value) => value,
    },
    {
      id: "sopProducts",
      kpi: "sopProducts",
      title: tableColumns["sopProducts"],
      sortable: true,
      desktopCellClassName: "GroupSopReport__sopProducts",
      mobileFullWidth: true,
      renderValue: (label, row) => (
        <SopProductPreviews
          images={Array.isArray(row.productImages) ? row.productImages : []}
          total={Number(row.sopProductCount)}
          label={label}
          onClick={() => onProductClick(row)}
        />
      ),
    },
  ];

  return [columns[0], ...columns.slice(1).filter(({ kpi }) => includedKpis.includes(kpi))];
};

export const getNextGroupSopSortState = (
  columnSortState: Record<string, SortDirection | undefined>,
  kpi: string,
): Record<string, SortDirection> => {
  const currentDirection = columnSortState[kpi];
  const defaultDirection: SortDirection = kpi === "aboName" ? "asc" : "desc";
  const nextDirection = currentDirection === "asc" ? "desc" : currentDirection === "desc" ? "asc" : defaultDirection;
  return { [kpi]: nextDirection };
};

export default function GroupSopReport() {
  const { t, i18n } = useTranslation(["actionReportGroupSop", "groupManagement", "navigation"]);
  const title = t("linkTitle.groupSopTracking", navigationContent.linkTitle.groupSopTracking, { ns: "navigation" });
  const { configuration } = useAppSelector((store) => store.boot);
  const selectedLeader = useAppSelector(selectGroupLeader);
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<SOP_TABS>(SOP_TABS.ORDER_RENEWAL);
  const [columnSortState, setColumnSortState] = useState<Record<string, SortDirection | undefined>>({});
  const [searchText, setSearchText] = useState("");
  const selectedLeaderName = selectedLeader
    ? (GROUP_SOP_MOCK_LEADERS.find((leader) => leader.aff === selectedLeader.aff && leader.abo === selectedLeader.abo)
        ?.name ?? `${selectedLeader.aff}-${selectedLeader.abo}`)
    : null;
  const tableColumns = {
    aboName: t("table.nameNumber", defaultContent.table.nameNumber),
    personalPV: t("table.ppv", defaultContent.table.ppv),
    customerPV: t("table.customerPv", defaultContent.table.customerPv),
    currentSopPv: t("table.currentSopPv", defaultContent.table.currentSopPv),
    expectedSopPv: t("table.expectedSopPv", defaultContent.table.expectedSopPv),
    sopProducts: t("table.sopProducts", defaultContent.table.sopProducts),
  };
  const handleProductClick = useCallback(
    (row: BaseABOData) => {
      dispatch(
        showProfile({
          affAbo: `${row.aff}-${row.abo}`,
          isFromActionReport: true,
          downlineProfileInitialTab: PROFILE_TABS.SOP_STATUS,
        }),
      );
    },
    [dispatch],
  );
  const selectedKpis = GROUP_SOP_TAB_CONFIG[activeTab].selectedKpis;
  const fieldConfig = createGroupSopFieldConfig(tableColumns, handleProductClick, selectedKpis);
  const activeRecords = useMemo(() => {
    const records = GROUP_SOP_MOCK_DATA[activeTab].filter(
      (record) => !selectedLeader || getGroupSopMockLeaderAbo(record.abo) === selectedLeader.abo,
    );
    const activeSort = Object.entries(columnSortState).find(([, direction]) => direction);
    if (!activeSort) return records;

    const [kpi, direction] = activeSort as [string, SortDirection];
    return [...records].sort((left, right) => {
      const leftValue = getSortValue(left, kpi);
      const rightValue = getSortValue(right, kpi);
      const comparison =
        typeof leftValue === "string" && typeof rightValue === "string"
          ? leftValue.localeCompare(rightValue)
          : Number(leftValue) - Number(rightValue);
      return direction === "asc" ? comparison : -comparison;
    });
  }, [activeTab, columnSortState, selectedLeader]);
  const reportData = useMemo<BaseABOData[]>(
    () =>
      activeRecords.map(({ currentPPV, currentSopPv, expectedSopPv, personalPV, customerPV, ...record }) => ({
        ...record,
        personalPV: formatSopValue(personalPV, i18n.language),
        customerPV: formatSopValue(customerPV, i18n.language),
        currentPPV: formatSopValue(currentPPV, i18n.language),
        currentSopPv: formatSopValue(currentSopPv, i18n.language),
        expectedSopPv: formatSopValue(expectedSopPv, i18n.language),
        sopProducts: t("table.viewSopProducts", defaultContent.table.viewSopProducts),
      })),
    [activeRecords, i18n.language, t],
  );
  const searchResults = useMemo<SearchSelectItem[]>(
    () =>
      reportData.map((record) => ({
        key: `${record.aff}-${record.abo}`,
        label: record.aboName,
        sublabel: `#${record.aboNumber}`,
      })),
    [reportData],
  );
  const reportLength = activeRecords.length;
  const handleSearchCallback = useCallback<SearchCallback>(
    (query) => {
      const normalizedQuery = query.trim().toLocaleLowerCase(i18n.language);
      return searchResults.filter(
        ({ label, sublabel }) =>
          label.toLocaleLowerCase(i18n.language).includes(normalizedQuery) || sublabel?.includes(normalizedQuery),
      );
    },
    [i18n.language, searchResults],
  );
  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => ({ displayName: searchResults.find((result) => result.key === key)?.label ?? "" }),
    [searchResults],
  );
  const handleColumnHeaderClick = useCallback((kpi: string) => {
    setColumnSortState((previous) => getNextGroupSopSortState(previous, kpi));
  }, []);

  return (
    <main className="GroupSopReport">
      <ActionReportHeader title={title} description="" reportLength={reportLength} showTimeStamp />
      <ActionReportToolbar
        reportLength={reportLength}
        treeViewToggle={{
          enabled: !configuration.actionReports?.disableTreeView,
          isTreeView: false,
          onToggle: () => {},
          disabled: false,
          label: "",
        }}
        search={{
          enabled: true,
          searchCallback: handleSearchCallback,
          selectionCallback: handleSelectionCallback,
          searchText: searchText,
          clearFunction: () => setSearchText(""),
          disabled: false,
        }}
        export={{
          moduleId: "groupSop",
          exportAffAbos: [],
          selectedKpis: [],
          confirmationMessage: t("exportModal.description", defaultContent.exportModal.description),
        }}
        groupLeaderFilterSlot={<GroupLeaderFilter leaders={GROUP_SOP_MOCK_LEADERS} tree={GROUP_SOP_MOCK_LEADER_TREE} />}
      />

      <section className="GroupSopReport__sopOverviewCard">
        <div className="GroupSopReport__sopOverviewCardTitle">
          {selectedLeaderName && <h3>{selectedLeaderName}</h3>}
          <h3>{t("overviewTitle", defaultContent.overviewTitle)}</h3>
        </div>
        <GroupSopOverview
          sopMonths={selectedLeader ? (mockForcastedSopByLeader[selectedLeader.abo] ?? []) : mockForcastedSop}
        />
      </section>

      <div className="GroupSopReport__tabs" role="tablist">
        <button
          className={`GroupSopReport__tab${activeTab === SOP_TABS.ORDER_RENEWAL ? " GroupSopReport__tab--active" : ""}`}
          role="tab"
          aria-selected={activeTab === SOP_TABS.ORDER_RENEWAL}
          onClick={() => setActiveTab(SOP_TABS.ORDER_RENEWAL)}
        >
          {t("tabs.orderRenewal", defaultContent.tabs.orderRenewal, {
            count: GROUP_SOP_MOCK_DATA[SOP_TABS.ORDER_RENEWAL].filter(
              (record) => !selectedLeader || getGroupSopMockLeaderAbo(record.abo) === selectedLeader.abo,
            ).length,
          })}
        </button>
        <button
          className={`GroupSopReport__tab${activeTab === SOP_TABS.PV_THIS_MONTH ? " GroupSopReport__tab--active" : ""}`}
          role="tab"
          aria-selected={activeTab === SOP_TABS.PV_THIS_MONTH}
          onClick={() => setActiveTab(SOP_TABS.PV_THIS_MONTH)}
        >
          {t("tabs.sopPvThisMonth", defaultContent.tabs.sopPvThisMonth, {
            count: GROUP_SOP_MOCK_DATA[SOP_TABS.PV_THIS_MONTH].filter(
              (record) => !selectedLeader || getGroupSopMockLeaderAbo(record.abo) === selectedLeader.abo,
            ).length,
          })}
        </button>
        <button
          className={`GroupSopReport__tab${activeTab === SOP_TABS.NO_PV_THIS_MONTH ? " GroupSopReport__tab--active" : ""}`}
          role="tab"
          aria-selected={activeTab === SOP_TABS.NO_PV_THIS_MONTH}
          onClick={() => setActiveTab(SOP_TABS.NO_PV_THIS_MONTH)}
        >
          {t("tabs.noSopPvThisMonth", defaultContent.tabs.noSopPvThisMonth, {
            count: GROUP_SOP_MOCK_DATA[SOP_TABS.NO_PV_THIS_MONTH].filter(
              (record) => !selectedLeader || getGroupSopMockLeaderAbo(record.abo) === selectedLeader.abo,
            ).length,
          })}
        </button>
      </div>
      <div className="GroupSopReport__table">
        <ActionReportListView
          selectedKpis={selectedKpis}
          reportId="sop"
          reportData={reportData}
          fieldConfig={fieldConfig}
          enableWatchlist={true}
          showImageBlock={true}
          aboHeader="nameNumber"
          onColumnHeaderClick={handleColumnHeaderClick}
          columnSortState={columnSortState}
          skipEnrichment
          mobileCardStyle="no-divider"
        />
      </div>
    </main>
  );
}
